<template>
  <vs-wrapper>
    <vs-text-input
      label="Name"
      :value="props.locationName"
      required
      data-test="name-field"
      @input="$emit('location-name-input', $event)"
    />

    <auto-complete
      :items="locationTypes"
      :value="props.locationTypeId"
      label="Location type"
      data-test="location-type-field"
      required
      @input="$emit('location-type-input', $event)"
    />

    <auto-complete
      v-if="isHubUser"
      :items="ownerOptions"
      :value="props.locationOwnerId"
      :disabled="ownerOptions && ownerOptions.length <= 1"
      label="Location owner"
      required
      data-test="location-owner-field"
      @input="$emit('location-owner-input', $event)"
    />
    <div v-else>
      <v-radio-group
        v-model="isLocationOwnedByCurrentOrganizationToggle"
        mandatory
        @change="onOwnerRadioChanged"
      >
        <template #label>
          <vs-heading type="subhead"
            >Is the location owned by {{ currentHubOwner?.name ?? 'one of  your clients' }}?
          </vs-heading>
        </template>
        <v-radio label="Yes" :value="true"></v-radio>
        <v-radio label="No" :value="false"></v-radio>
      </v-radio-group>
      <v-checkbox
        v-if="!isLocationOwnedByCurrentOrganizationToggle"
        v-model="privateToggle"
        label="Private"
        hint="Only your company and your clients see this location"
        class="mt-0"
        persistent-hint
        @change="onPrivateChanged"
      ></v-checkbox>
    </div>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useHubs } from '@/common/useHubs'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import { useGetLocationTypesQuery } from '@/api/location-types'
import { sortBy } from 'lodash'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import AutoComplete from '@/components/common/AutoComplete.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { useStore } from 'vuex-composition-helpers'
import VsHeading from '@/components/vision/VsHeading.vue'

const props = defineProps<{
  locationName?: string | null
  locationTypeId?: number | null
  locationOwnerId?: string | null
  locationPrivate?: boolean | null
}>()

const emit = defineEmits([
  'location-owner-input',
  'location-type-input',
  'location-name-input',
  'location-private-input',
])

const { currentHubOwner } = useHubs()
const { currentOrg } = useCurrentOrg()
const store = useStore()

const isHubUser = computed<boolean>(() => store.getters['permissions/isHostOrganizationUser'])
const isLocationOwnedByCurrentOrganizationToggle = ref(
  props.locationOwnerId !== currentOrg.value?.id,
)
const privateToggle = ref(!!props.locationPrivate)
watch(
  () => props.locationOwnerId,
  (newVal) => {
    isLocationOwnedByCurrentOrganizationToggle.value = newVal !== currentOrg.value?.id
  },
  { immediate: true },
)
watch(
  () => props.locationPrivate,
  (newVal) => {
    privateToggle.value = !!newVal
  },
  { immediate: true },
)
const ownerOptions = computed(() => {
  if (!currentOrg.value) return []
  return [
    {
      value: currentOrg.value.id,
      text: currentOrg.value.name,
    },
  ]
})
const { data: locTypeQueryData } = useGetLocationTypesQuery({
  includeUnused: true,
})
const locationTypes = computed(() => {
  return sortBy(
    locTypeQueryData.value?.map((locationType) => ({
      text: locationType.name,
      value: locationType.id,
      icon: locationType.iconId || 'mdi-map-marker',
    })),
    'text',
  )
})

function onOwnerRadioChanged(e: boolean) {
  if (!e) {
    emit('location-owner-input', currentOrg.value?.id ?? '')
  } else {
    emit('location-owner-input', currentHubOwner.value?.id ?? '')
    emit('location-private-input', false)
  }
}

function onPrivateChanged(e: boolean) {
  emit('location-private-input', e)
}
</script>
